import { ConductorSurface } from "@faire/web-api/indigofair/data/ConductorSurface";
import React, { useMemo, useState } from "react";
import { Storage } from "@faire/web/common/Storage";
import addDays from "date-fns/addDays";
import { useConductorComponent } from "@faire/retailer/features/Conductor/useConductorComponent";
import { RenderWithBuilder } from "@faire/web/builder/BuilderComponents/RenderWithBuilder";
import { BottomSheet } from "../BottomSheet/BottomSheet";
import { trackRetailerMobileAppDownloadNudgeDismissDismiss } from "@faire/web-api/events/retailerMobile/dismiss/appDownloadNudgeDismiss";
import { trackRetailerMobileAppDownloadNudgeViewView } from "@faire/web-api/events/retailerMobile/view/appDownloadNudgeView";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useLocation, withRouter } from "react-router-dom";
import { useQuery as useShowNuxAppDownloadNudgeQuery } from "@faire/web-api/api/retailer/show-nux-app-download-nudge/get-hooks";
import { useMilestone } from "@faire/retailer/stores/domain/useMilestone";
import { useSettingAppPromotionBannerLinks } from "@faire/retailer/settings/useSettingAppPromotionBannerLinks";
import { getDeepLinkUrl, OSType } from "@faire/retailer/services/MobileAppBanner";
import { getOsName } from "@faire/retailer/lib/user-agent/osName";
import { getIsAppInstalled } from "@faire/retailer/serialized-data/getIsAppInstalled";
import { withQueryParams } from "@faire/web/common/query-params/withQueryParams";
import { IRawQueryParamsProps } from "@faire/web/common/query-params/IQueryParamsProps";

export const APP_DOWNLOAD_NUDGE_DISMISSED_TIMESTAMP = "retailer_mobile-app-download-nudge-timestamp";
const DISMISS_INTERVAL_DAYS = 1;

const AppDownloadNudgeBase: React.FC<IRawQueryParamsProps> = ({ queryParams }) => {
  const location = useLocation();
  const { data, isLoading } = useShowNuxAppDownloadNudgeQuery();
  const { incrementCount: incrementDismissCount } = useMilestone("APP_DOWNLOAD_NUDGE_DISMISSED");
  const storeLinks = useSettingAppPromotionBannerLinks();
  const { conductorBuilderModelName } = useConductorComponent(
    ConductorSurface.SITE_WIDE_MODAL // TODO: change to new surface once BE is updated
  );
  const ref = useDoActionWhenVisible(() => {
    trackRetailerMobileAppDownloadNudgeViewView(location.pathname);
  });

  const [isOpen, setIsOpen] = useState<boolean>(true);

  const shouldRender =
    !!conductorBuilderModelName &&
    !isLoading &&
    !!data?.should_show_nudge;

  const deepLinkUrl = getDeepLinkUrl(location, queryParams);
  const storeLinkUrl = useMemo(() => {
    if (getOsName() === OSType.IOS) {
      return storeLinks.app_store_link;
    } else if (getOsName() === OSType.ANDROID) {
      return storeLinks.play_store_link;
    }
    return undefined;
  }, [storeLinks.app_store_link, storeLinks.play_store_link]);
  const ctaUrl = useMemo((): string | undefined => (
    (!!getIsAppInstalled() && deepLinkUrl) || storeLinkUrl
  ), [deepLinkUrl, storeLinkUrl]);

  const handleClose = () => {
    incrementDismissCount();
    Storage.local.setItem(
      APP_DOWNLOAD_NUDGE_DISMISSED_TIMESTAMP,
      addDays(new Date(), DISMISS_INTERVAL_DAYS).getTime().toString()
    );
    trackRetailerMobileAppDownloadNudgeDismissDismiss(location.pathname);
    setIsOpen(false);
  };

  if (shouldRender) {
    return (
      <div data-test-id="app-download-nudge" ref={ref}>
        <BottomSheet
          condensed
          isOpen={isOpen}
          onClose={handleClose}
        >
          <RenderWithBuilder
            modelName={conductorBuilderModelName}
            data={{ ctaUrl: ctaUrl }}
          />
        </BottomSheet>
      </div>
    );
  }

  return null;
};

export const AppDownloadNudge = withRouter(
  withQueryParams(AppDownloadNudgeBase)
);
